<template>
  <themed-modal
    name="task-document-picker"
    title="Attach Existing Document"
    size="lg"
    :scrollable="true"
  >
    <div class="task-document-picker">
      <input
        v-model="search"
        type="text"
        placeholder="Search documents by name..."
        class="w-full rounded-md border px-3 py-2 text-sm mb-4 bg-background border-input text-foreground"
        @input="onSearchInput"
      />

      <div v-if="loading" class="text-sm text-muted-foreground py-4 text-center">
        Searching...
      </div>

      <div v-else-if="search.length >= 2 && results.length === 0" class="text-sm text-muted-foreground py-4 text-center">
        No documents found.
      </div>

      <div v-else-if="search.length < 2" class="text-sm text-muted-foreground py-4 text-center">
        Type at least 2 characters to search.
      </div>

      <ul v-else class="space-y-1 max-h-64 overflow-y-auto">
        <li
          v-for="doc in results"
          :key="doc.id"
          class="flex items-center justify-between p-2 rounded-md hover:bg-muted cursor-pointer text-sm"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="font-medium truncate">{{ doc.name }}</span>
            <span class="text-muted-foreground text-xs flex-shrink-0">{{ doc.type }}</span>
          </div>
          <themed-button
            size="sm"
            variant="outline"
            :disabled="attachingId === doc.id"
            @click="selectDocument(doc)"
          >
            {{ attachingId === doc.id ? '...' : 'Attach' }}
          </themed-button>
        </li>
      </ul>
    </div>

    <template #footer>
      <themed-button variant="outline" @click="close">Cancel</themed-button>
    </template>
  </themed-modal>
</template>

<script>
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
  name: 'TaskDocumentPicker',

  components: { ThemedModal, ThemedButton },

  props: {
    taskId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      search: '',
      results: [],
      loading: false,
      attachingId: null,
      debounceTimer: null,
    };
  },

  methods: {
    close() {
      this.$modal.hide('task-document-picker');
      this.search = '';
      this.results = [];
    },

    onSearchInput() {
      clearTimeout(this.debounceTimer);
      if (this.search.length < 2) {
        this.results = [];
        return;
      }
      this.debounceTimer = setTimeout(this.fetchDocuments, 300);
    },

    async fetchDocuments() {
      this.loading = true;
      try {
        const res = await axios.get('/api/documents', {
          params: { search: this.search, per_page: 20 },
        });
        this.results = res.data.data || res.data;
      } catch (e) {
        console.error('Document search failed:', e);
      } finally {
        this.loading = false;
      }
    },

    async selectDocument(doc) {
      if (!this.taskId || this.attachingId) return;
      this.attachingId = doc.id;
      try {
        const res = await axios.post(`/api/tasks/${this.taskId}/documents/attach`, {
          document_id: doc.id,
        });
        this.$emit('document-attached', res.data);
        this.$toast.success(`${doc.name} attached to task.`);
        this.close();
      } catch (e) {
        this.$toast.error('Failed to attach document.');
      } finally {
        this.attachingId = null;
      }
    },
  },
};
</script>
